import { useCallback, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServices } from "@/services/ServiceProvider";
import { useMyWorkspaces } from "@/hooks/useMyWorkspaces";
import { useAuthStore } from "@/stores/useAuthStore";
import type { ThreadUnreadCounts } from "@/services/interfaces/IWorkspaceService";
import type { ThreadKey } from "@/lib/workspace/threads";

/**
 * Unread workspace messages, per quest and per thread, via 10 s polling.
 *
 * Per-thread counts come from get_thread_unread_counts (20260924100000) and
 * exclude the reader's own and system messages. Before that migration is
 * applied the service falls back to the legacy per-quest count, which shows
 * up in the quest totals but on no tab.
 *
 * There is no whole-quest `markRead` here: the legacy per-quest cursor
 * (workspace_read_cursors) is the server-side fallback read point for any
 * thread without its own cursor, so writing it would silently zero every
 * thread's unread for that quest, including tabs the caller never viewed.
 * Only `markThreadRead` — which writes one thread's cursor — is exposed.
 *
 * NOTE: Supabase Realtime is off under Privy token-exchange auth (reconnect
 * storms), so this polls.
 */
export function useUnreadCounts() {
  const { workspace } = useServices();
  const queryClient = useQueryClient();
  const userId = useAuthStore((s) => s.session?.userId);
  const { data: workspaces = [] } = useMyWorkspaces();
  const questIds = workspaces.map((ws) => ws.questId);
  // The counts are the reader's own (JWT-derived), so the key names the reader:
  // two accounts on one browser can share the exact same quest list.
  const queryKey = ["thread-unread-counts", userId, questIds.join(",")];

  const { data: byThread = new Map() as ThreadUnreadCounts } = useQuery({
    queryKey,
    // get_thread_unread_counts answered for every quest the reader belongs to,
    // on BOTH networks. The adapter now passes the active chain (p_chain_id,
    // 20261015120000) but retries unscoped while that migration is not
    // applied, so keep this filter: useMyWorkspaces is already scoped to the
    // active chain, and without it Mainnet unread lights the Testnet badge
    // (72 on a Testnet inbox holding two read chats).
    // The key needs no chain id: the chain is fixed for a page load, and the
    // network switch reloads the page (useNetworkStore.switchChain).
    queryFn: async () => {
      const all = await workspace.getThreadUnreadCounts(questIds);
      const mine = new Set(questIds);
      return new Map([...all].filter(([questId]) => mine.has(questId))) as ThreadUnreadCounts;
    },
    enabled: questIds.length > 0,
    staleTime: 10_000,
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
  });

  const unreadCounts = useMemo(() => {
    const totals = new Map<string, number>();
    for (const [questId, threads] of byThread) {
      let sum = 0;
      for (const n of threads.values()) sum += n;
      if (sum > 0) totals.set(questId, sum);
    }
    return totals;
  }, [byThread]);

  const totalUnread = useMemo(() => {
    let sum = 0;
    for (const n of unreadCounts.values()) sum += n;
    return sum;
  }, [unreadCounts]);

  // What badges show: chats waiting on the reader, not messages. Three replies
  // in two chats is two, the same number the inbox's Unread section lists.
  const unreadChats = unreadCounts.size;

  const threadUnread = useCallback(
    (questId: string, thread: ThreadKey) => byThread.get(questId)?.get(thread) ?? 0,
    [byThread],
  );

  const markThreadRead = useCallback(
    async (questId: string, thread: ThreadKey, readUpTo?: string) => {
      // A poll already in flight was read before this mark; letting it land
      // after the optimistic zero would bring the badge back for ~10s.
      await queryClient.cancelQueries({ queryKey });
      queryClient.setQueryData(queryKey, (old: ThreadUnreadCounts | undefined) => {
        const next: ThreadUnreadCounts = new Map(old ?? []);
        const threads = new Map(next.get(questId) ?? []);
        threads.delete(thread);
        next.set(questId, threads);
        return next;
      });
      await workspace.markThreadRead(questId, thread, readUpTo);
    },
    // queryKey is derived from questIds each render; its string is the identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- queryKey is rebuilt every render; questIds.join stands in for it
    [workspace, queryClient, userId, questIds.join(",")],
  );

  return { unreadCounts, totalUnread, unreadChats, threadUnread, markThreadRead };
}
